import { cn } from '@repo/tailwind-config/tailwind.functions'
import { ProviderIcon } from '@repo/ui/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { getCenterLocalOrganizers } from '../../../../api/centers/center-local-organizers/get-center-local-organizers'
import { updateCenterLocalOrganizers } from '../../../../api/centers/center-local-organizers/update-center-local-organizers'
import { getCenter } from '../../../../api/centers/get-center'
import { useObserver } from '../../../../lib/contexts/observer'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import useTranslate from '../../../../lib/hooks/use-translate'
import { Center } from '../../../../lib/types/models/center/center'
import {
  CenterLocalOrganizer,
  CenterLocalOrganizerInput
} from '../../../../lib/types/models/center/center-local-organizer'
import { DataProps } from '../../../../lib/types/utils'
import EditableTable from '../../../elements/editable-table'
import { useCenterCosts } from './center.hooks'
import {
  centerCostsTableColorMapping,
  CenterCostsTableFooter,
  CenterCostsTableHeader,
  CenterTableSkeleton
} from './center.ui'
import {
  CenterCostsTableProps,
  getCenterLocalOrganizersColumns,
  mapCenterLocalOrganizerCostToInput
} from './utils'

export function CenterLocalOrganizersCostsTable(props: DataProps<Center, CenterCostsTableProps>) {
  const { mode, costType, currency } = props
  const { t } = useTranslate()
  const { user } = useAuth()
  const [localOrganizers, setLocalOrganizers] = useState<CenterLocalOrganizerInput[]>()
  const [editable, setEditable] = useState(false)

  const {
    id: centerId,
    data: center,
    refetch: refetchCenter
  } = useDataProps(props, (id) => getCenter(id, 'FULL'))

  const { centerCostsView, lock, unlock } = useCenterCosts({
    centerId,
    costType,
    table: 'local-organizers'
  })

  const { data: centerLocalOrganizers, refetch } = useQuery({
    queryKey: ['center-local-organizers', centerId],
    queryFn: () => getCenterLocalOrganizers(centerId)
  })

  useObserver({
    key: 'center-local-organizers-costs-table',
    update: refetch
  })

  const { mutateAsync } = useMutation({
    mutationFn: (data: CenterLocalOrganizerInput[]) => updateCenterLocalOrganizers(centerId, data),
    onSuccess: () => {
      setLocalOrganizers(undefined)
    }
  })

  if (!user || !centerLocalOrganizers || !center) return <CenterTableSkeleton />
  return (
    <div>
      <CenterCostsTableHeader
        mode={mode}
        icon={ProviderIcon}
        title={t('center.costs.local.organisers.fees.title')}
        onCancel={() => setLocalOrganizers(undefined)}
        onEditableChange={setEditable}
        validateOptions={{
          validate: costType === 'final-cost',
          locked: center.isLocalOrganizersLocked,
          onLock: lock.mutateAsync,
          onUnlock: unlock.mutateAsync
        }}
        onSave={async () => {
          if (localOrganizers) {
            await mutateAsync(localOrganizers)
            await Promise.all([refetch(), centerCostsView.refetch(), refetchCenter?.()])
          }
        }}
      />

      <EditableTable<CenterLocalOrganizer>
        isEditable={editable}
        data={centerLocalOrganizers.data}
        columns={getCenterLocalOrganizersColumns(costType, currency, t)}
        onDataChange={(data) => {
          const mappedData = data.map((item) => mapCenterLocalOrganizerCostToInput(item, costType))
          if (mode === 'free-edit') mutateAsync(mappedData)
          else setLocalOrganizers(mappedData)
        }}
        footerRowClassName={cn({ 'bg-white': costType === 'final-cost' })}
        footer={
          <CenterCostsTableFooter
            currency={currency}
            costType={costType}
            textColor={centerCostsTableColorMapping[user.role][costType].textColor}
            variant={centerCostsTableColorMapping[user.role][costType].variant}
            label={t('center.costs.local.organisers.fees.table.footer.total.fees')}
            total={centerCostsView.data?.totalFees}
            initialAmount={centerCostsView.data?.totalFees}
            finalAmount={centerCostsView.data?.finalTotalFees}
            balance={centerCostsView.data?.feesBalance}
          />
        }
      />
    </div>
  )
}
